import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertTriangle } from 'lucide-react';

const CONFIRMATION_WORD = 'DELETAR';

export const DeleteConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title = 'Confirmar Exclusão',
  description = 'Esta ação não pode ser desfeita.',
  itemName = '',
  itemType = 'item',
  loading = false
}) => {
  const [inputValue, setInputValue] = useState('');
  
  const isConfirmed = inputValue === CONFIRMATION_WORD;

  const handleConfirm = () => {
    if (isConfirmed) {
      onConfirm();
      setInputValue('');
    }
  };

  const handleClose = () => {
    setInputValue('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]" data-testid="delete-confirmation-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive" data-testid="delete-modal-title">
            <AlertTriangle className="w-5 h-5" />
            {title}
          </DialogTitle>
          <DialogDescription className="text-left space-y-2" data-testid="delete-modal-description">
            <p>{description}</p>
            {itemName && (
              <p className="font-medium text-foreground">
                {itemType}: <span className="text-destructive">{itemName}</span>
              </p>
            )}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
            <p className="text-sm text-destructive font-medium mb-2">
              Para confirmar a exclusão, digite a palavra abaixo:
            </p>
            <p className="text-center text-2xl font-bold text-destructive tracking-widest">
              {CONFIRMATION_WORD}
            </p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="confirmation-input">Digite "{CONFIRMATION_WORD}" para confirmar</Label>
            <Input
              id="confirmation-input"
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value.toUpperCase())}
              placeholder={CONFIRMATION_WORD}
              className={`text-center font-mono text-lg tracking-wider ${
                inputValue.length > 0 && !isConfirmed 
                  ? 'border-destructive focus-visible:ring-destructive' 
                  : isConfirmed 
                    ? 'border-green-500 focus-visible:ring-green-500' 
                    : ''
              }`}
              data-testid="delete-confirmation-input"
              autoComplete="off"
            />
            {inputValue.length > 0 && !isConfirmed && (
              <p className="text-xs text-destructive text-center">
                A palavra não corresponde. Digite exatamente "{CONFIRMATION_WORD}"
              </p>
            )}
            {isConfirmed && (
              <p className="text-xs text-green-600 text-center">
                Confirmação correta. Você pode prosseguir com a exclusão.
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
            data-testid="delete-cancel-button"
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={!isConfirmed || loading}
            data-testid="delete-confirm-button"
          >
            {loading ? 'Excluindo...' : 'Excluir Permanentemente'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteConfirmationModal;
